import React from 'react';
import { Container, Row, Col, Card, Button, Table } from 'react-bootstrap';

// Income limits from the 2026 Federal Poverty Guidelines (100%, 133%, 150%, 200%, 250%),
// mapped to tuition reduction tiers of 100%, 95%, 85%, 75%, and 40%.
const assistanceTable = [
  { household: 1, limits: ['$15,960', '$21,227', '$23,940', '$31,920', '$39,900'] },
  { household: 2, limits: ['$21,640', '$28,781', '$32,460', '$43,280', '$54,100'] },
  { household: 3, limits: ['$27,320', '$36,336', '$40,980', '$54,640', '$68,300'] },
  { household: 4, limits: ['$33,000', '$43,890', '$49,500', '$66,000', '$82,500'] },
  { household: 5, limits: ['$38,680', '$51,444', '$58,020', '$77,360', '$96,700'] },
  { household: 6, limits: ['$44,360', '$58,999', '$66,540', '$88,720', '$110,900'] },
  { household: 7, limits: ['$50,040', '$66,553', '$75,060', '$100,080', '$125,100'] },
  { household: 8, limits: ['$55,720', '$74,108', '$83,580', '$111,440', '$139,300'] },
];

const reductionTiers = ['100%', '95%', '85%', '75%', '40%'];

const Fees: React.FC = () => {
  return (
    <Container className="py-5">
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold text-navy">Fees & Financial Assistance</h1>
        <p className="lead text-muted">Transparent pricing. Cost should never keep a kid off the field.</p>
        <hr className="w-25 mx-auto text-teal" style={{ opacity: 1, height: '3px' }} />
      </div>

      {/* Season Fees */}
      <h2 className="text-teal fw-bold mb-4">2026-27 Season Fees</h2>
      <Row className="g-4 mb-5">
        <Col md={4}>
          <Card className="h-100 border-0 shadow-sm text-center">
            <Card.Body className="p-4">
              <h3 className="h5 fw-bold text-navy">Academy (U9-U10)</h3>
              <p className="display-6 fw-bold text-navy mb-1">$245</p>
              <p className="text-muted small mb-0">per season</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 border-0 shadow-sm text-center">
            <Card.Body className="p-4">
              <h3 className="h5 fw-bold text-navy">Select (U11+)</h3>
              <p className="display-6 fw-bold text-navy mb-1">$750</p>
              <p className="text-muted small mb-0">per year</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 border-0 shadow-sm text-center">
            <Card.Body className="p-4">
              <h3 className="h5 fw-bold text-navy">Select (U15+)</h3>
              <p className="display-6 fw-bold text-navy mb-1">$420</p>
              <p className="text-muted small mb-0">Fall season only</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Select Payment Schedule */}
      <Row className="mb-5">
        <Col lg={10} className="mx-auto">
          <div className="bg-light p-4 rounded shadow-sm">
            <h2 className="text-teal fw-bold mb-3">Select Payment Schedule</h2>
            <p>
              Fees are invoiced through Stripe. Families can elect which payment model works best for them!
            </p>
            <Table responsive className="mb-0 bg-white rounded">
              <thead>
                <tr>
                  <th>Payment Plan</th>
                  <th>Due Dates</th>
                  <th>Amount per Payment</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Annual</td>
                  <td>Jul 15, 2026</td>
                  <td>$750</td>
                </tr>
                <tr>
                  <td>Bi-Annual</td>
                  <td>Jul 15, 2026 &amp; Jan 15, 2027</td>
                  <td>$375</td>
                </tr>
                <tr>
                  <td>Quarterly</td>
                  <td>Jul 15 &amp; Oct 15, 2026; Jan 15 &amp; Mar 31, 2027</td>
                  <td>$187.50</td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>

      {/* Financial Assistance */}
      <h2 className="text-teal fw-bold mb-3">Financial Assistance</h2>
      <Row className="mb-4">
        <Col lg={10}>
          <p>
            All families can have tuition reduced at the amounts in the table below based on family income.
            Find your household size, then the column with your annual household income — the percentage at
            the top of that column is your tuition reduction.
          </p>
          <p>
            If a special situation arises, please reach out to us at{' '}
            <a href="mailto:info@515soccer.org">info@515soccer.org</a> and we can help!
          </p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={10} className="mx-auto">
          <Table responsive bordered hover className="text-center align-middle shadow-sm">
            <thead>
              <tr className="table-dark">
                <th rowSpan={2} className="align-middle">Household Size</th>
                <th colSpan={5}>Tuition Reduction (annual household income at or below)</th>
              </tr>
              <tr className="table-secondary">
                {reductionTiers.map((tier) => (
                  <th key={tier}>{tier} off</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assistanceTable.map((row) => (
                <tr key={row.household}>
                  <td className="fw-bold">{row.household}</td>
                  {row.limits.map((limit, i) => (
                    <td key={i}>{limit}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="small text-muted">
            Income limits are based on the 2026 Federal Poverty Guidelines (100%, 133%, 150%, 200%, and 250%).
            For households with more than 8 people, contact us and we will calculate your tier.
          </p>
        </Col>
      </Row>

      <div className="text-center mb-5">
        <Button
          variant="primary"
          size="lg"
          href="https://forms.gle/QqQCD3JArjrdNYVf7"
          target="_blank"
          className="px-5 fw-bold"
        >
          Apply for Financial Assistance
        </Button>
        <p className="small text-muted mt-3 mb-0">
          Applications are confidential and reviewed promptly.
        </p>
      </div>

      <div className="text-center">
        <h3 className="fw-bold mb-3">Questions?</h3>
        <p>We're happy to walk you through fees, payment plans, or the assistance process.</p>
        <Button href="mailto:info@515soccer.org" variant="outline-primary" size="lg">Contact Us</Button>
      </div>
    </Container>
  );
};

export default Fees;
